import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../context/CurrencyContext';

// Стилизованный контейнер для компонента курса валют
const ExchangeRateContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 8px 0 14px;
  min-height: 36px; /* Фиксированная высота для предотвращения скачков */
`;

// Стилизованный текст курса валют с неоморфным дизайном
const ExchangeRateText = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 14px;
  font-size: 14px;
  color: #4a6fa1;
  font-weight: 500;
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.05),
    -4px -4px 8px rgba(255, 255, 255, 0.8);
  text-align: center;
  position: relative;
  transition: opacity 0.3s ease;
  
  /* Добавляем иконку валюты */
  &:before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    margin-right: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%234a6fa1'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1.93.82 1.62 2.02 1.62 1.19 0 1.78-.65 1.78-1.34 0-.84-.81-1.13-2.19-1.54-1.54-.45-3.4-1.08-3.4-3.05 0-1.45 1.12-2.7 3.1-3.05V5.5h2.67v1.82c1.68.3 2.64 1.52 2.74 3.11h-1.97c-.08-.92-.7-1.62-1.77-1.62-.98 0-1.7.53-1.7 1.28 0 .75.62 1.08 2.13 1.5 1.51.42 3.5.96 3.5 3.13-.01 1.73-1.34 2.81-3.03 3.14z'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    opacity: 0.7;
  }
  
  &.loading {
    position: relative;
    overflow: hidden;
    
    &:after {
      content: "";
      position: absolute;
      top: -50%;
      left: -50%;
      right: -50%;
      bottom: -50%;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: rotate(30deg) translateY(0) translateX(0);
      animation: shimmer 1.5s infinite linear;
    }
  }
  
  &.changing {
    animation: fadeInOut 0.5s ease;
  }
  
  @keyframes shimmer {
    0% { transform: rotate(30deg) translateX(-100%); }
    100% { transform: rotate(30deg) translateX(100%); }
  }
  
  @keyframes fadeInOut {
    0% { opacity: 0.5; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
  }
  
  @media (max-width: 400px) {
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 12px;
  }
`;

// Доступные валюты для ротации (расширенный список)
const AVAILABLE_CURRENCIES = [
  'EUR', 'UAH', 'RUB', 'GBP', 'PLN', 'JPY', 'CNY', 'INR', 
  'CAD', 'AUD', 'BRL', 'CHF', 'TRY', 'ZAR', 'IDR', 'KRW'
];

// Резервные значения курсов на случай ошибок с API
const FALLBACK_RATES = {
  'EUR': 0.92,
  'UAH': 41.5,
  'RUB': 93.8,
  'GBP': 0.79,
  'PLN': 3.94,
  'JPY': 151.2,
  'CNY': 7.24,
  'INR': 83.5,
  'CAD': 1.36,
  'AUD': 1.51,
  'BRL': 5.13,
  'CHF': 0.89,
  'TRY': 32.1,
  'ZAR': 18.3,
  'IDR': 15800,
  'KRW': 1350
};

// Символы валют
const CURRENCY_SYMBOLS = {
  'UAH': '₴',
  'USD': '$',
  'EUR': '€',
  'RUB': '₽',
  'GBP': '£',
  'PLN': 'zł',
  'JPY': '¥',
  'CNY': '¥',
  'INR': '₹',
  'CAD': 'C$',
  'AUD': 'A$',
  'BRL': 'R$',
  'CHF': 'Fr',
  'TRY': '₺',
  'ZAR': 'R',
  'IDR': 'Rp',
  'KRW': '₩'
};

const ExchangeRate = () => {
  const { t } = useTranslation();
  
  // Получаем контекст валюты
  const context = useCurrency();
  
  // Используем состояние для отслеживания текущей валюты пользователя
  const [actualCurrency, setActualCurrency] = useState('USD');
  
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [currentCurrencyIndex, setCurrentCurrencyIndex] = useState(0);
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  
  // Эффект для отслеживания изменений в контексте валюты
  // Используем все возможные свойства контекста
  useEffect(() => {
    // Обновляем обработчик события изменения валюты
    const handleCurrencyChange = () => {
      const currencyFromContext = 
        context.preferredCurrency || 
        context.userCurrency || 
        context.currency;
      
      if (currencyFromContext) {
        console.log("Currency changed in context:", currencyFromContext);
        setActualCurrency(currencyFromContext);
        
        // Если не USD, сразу устанавливаем targetCurrency для предотвращения мигания
        if (currencyFromContext !== 'USD') {
          setTargetCurrency(currencyFromContext);
          setIsChanging(false); // Предотвращаем анимацию при инициализации
        }
      } else {
        console.log("No currency found in context, using default USD");
        setActualCurrency('USD');
      }
    };
    
    // Вызываем при первом рендере и при изменении контекста
    handleCurrencyChange();
    
    // Если в контексте есть специальный метод для подписки на изменения, используем его
    if (context.subscribe && typeof context.subscribe === 'function') {
      return context.subscribe(handleCurrencyChange);
    }
    
    // Иначе полагаемся на обычный рендеринг через изменение самого context
  }, [context]);
  
  // Определяем символ валюты
  const getCurrencySymbol = (currency) => {
    return CURRENCY_SYMBOLS[currency] || currency;
  };

  // Запрос на получение всех курсов валют
  const fetchExchangeRates = useCallback(async () => {
    try {
      setLoading(true);
      
      // Пробуем оба возможных эндпоинта
      let response;
      try {
        response = await fetch('https://fftg.farodinprojects.org:3000/exchange_rates');
        if (!response.ok) {
          response = await fetch('https://fftg.farodinprojects.org:3000/get_exchange_rates');
        }
      } catch (e) {
        // Если первый эндпоинт недоступен, пробуем альтернативный
        response = await fetch('https://fftg.farodinprojects.org:3000/get_exchange_rates');
      }
      
      if (!response.ok) {
        throw new Error('Exchange rates API not available');
      }
      
      const data = await response.json();
      console.log("API response data:", data);
      
      // Проверяем различные форматы ответа от API
      if (data && data.rates) {
        setRates(data.rates);
        console.log("Using data.rates:", data.rates);
      } else if (data && typeof data === 'object') {
        // Если данные пришли напрямую как объект курсов
        setRates(data);
        console.log("Using direct rates object:", data);
      } else {
        throw new Error('Invalid data format from exchange rates API');
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      // Используем резервные значения
      setRates(FALLBACK_RATES);
      console.log("Using fallback rates:", FALLBACK_RATES);
    } finally {
      setLoading(false);
    }
  }, []);

  // Обновляем курсы при изменении валюты или при первом рендере
  useEffect(() => {
    fetchExchangeRates();
    
    // Обновляем курсы каждые 30 минут
    const interval = setInterval(fetchExchangeRates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchExchangeRates]);
  
  // Эффект для определения, как отображать курсы
  useEffect(() => {
    console.log("actualCurrency changed to:", actualCurrency);
    
    // Останавливаем предыдущий интервал ротации перед установкой нового
    let rotationInterval;
    
    // Если валюта пользователя - USD, настраиваем ротацию
    if (actualCurrency === 'USD') {
      // Сбрасываем индекс и устанавливаем первую валюту
      setCurrentCurrencyIndex(0);
      setTargetCurrency(AVAILABLE_CURRENCIES[0]);
      console.log(`USD selected, starting rotation with: ${AVAILABLE_CURRENCIES[0]}`);
      
      // Настраиваем интервал для ротации валют
      rotationInterval = setInterval(() => {
        setIsChanging(true); // Начинаем анимацию
        
        setTimeout(() => {
          setCurrentCurrencyIndex(prev => {
            const nextIndex = (prev + 1) % AVAILABLE_CURRENCIES.length;
            const nextCurrency = AVAILABLE_CURRENCIES[nextIndex];
            console.log(`Rotating to: ${nextCurrency}`);
            setTargetCurrency(nextCurrency);
            return nextIndex;
          });
          setTimeout(() => setIsChanging(false), 100); // Заканчиваем анимацию
        }, 400); // Задержка для плавности анимации
        
      }, 5000); // Интервал ротации - 5 секунд
    } else {
      // Если валюта не USD, просто показываем курс USD к выбранной валюте
      console.log(`Non-USD currency: ${actualCurrency}, showing fixed rate`);
      setTargetCurrency(actualCurrency);
      setIsChanging(false);
    }
    
    // Очищаем интервал при размонтировании или изменении валюты
    return () => {
      if (rotationInterval) {
        clearInterval(rotationInterval);
        console.log("Cleared rotation interval");
      }
    };
  }, [actualCurrency]); // Зависимость от actualCurrency
  
  // Получаем текущий курс для отображения
  const getCurrentRate = () => {
    if (!rates || Object.keys(rates).length === 0 || !targetCurrency) {
      console.log("Missing rates or target currency", { rates, targetCurrency });
      return null;
    }
    
    // Прямой курс USD к целевой валюте
    const directRateKey = `USD_${targetCurrency}`;
    if (rates[directRateKey] !== undefined) {
      console.log(`Found direct rate ${directRateKey}: ${rates[directRateKey]}`);
      return rates[directRateKey];
    }
    
    // Прямой курс без префикса (возможно, API возвращает просто по коду валюты)
    if (rates[targetCurrency] !== undefined) {
      console.log(`Found simple rate for ${targetCurrency}: ${rates[targetCurrency]}`);
      return rates[targetCurrency];
    }
    
    // Обратный курс, если есть
    const reverseRateKey = `${targetCurrency}_USD`;
    if (rates[reverseRateKey] !== undefined) {
      const reverseRate = 1 / rates[reverseRateKey];
      console.log(`Using inverse of ${reverseRateKey}: ${reverseRate}`);
      return reverseRate;
    }
    
    // Для USD возвращаем 1
    if (targetCurrency === 'USD') {
      return 1;
    }
    
    // Если курс не найден, возвращаем из резервных значений
    console.log(`No rate found, using fallback for ${targetCurrency}: ${FALLBACK_RATES[targetCurrency]}`);
    return FALLBACK_RATES[targetCurrency] || null;
  };
  
  // Форматируем отображение курса с правильным числом десятичных знаков
  const formatRate = (rate) => {
    if (!rate || isNaN(rate)) return "0.00";
    
    // Выбираем количество десятичных знаков в зависимости от величины курса
    if (rate >= 100) return rate.toFixed(2);
    if (rate >= 10) return rate.toFixed(2);
    if (rate >= 1) return rate.toFixed(4);
    return rate.toFixed(6);
  };

  // Текст для отображения курса
  const rate = getCurrentRate();
  const displayText = loading || rate === null
    ? t('loading') 
    : `1 ${getCurrencySymbol('USD')} = ${formatRate(rate)} ${getCurrencySymbol(targetCurrency)}`;

  // Не отображаем компонент, если данные не загружены и нет fallback
  if (!loading && rate === null) {
    console.log("Not rendering exchange rate - no data available");
    return null;
  }

  return (
    <ExchangeRateContainer>
      <ExchangeRateText className={`${loading ? 'loading' : ''} ${isChanging ? 'changing' : ''}`}>
        {displayText}
      </ExchangeRateText>
    </ExchangeRateContainer>
  );
};

export default ExchangeRate;